import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { login as apiLogin, getPublicConfig } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useSystemConfig } from "../context/SystemConfigContext";
import Button from "../components/UI/Button";
import { useTranslation, I18nContext } from "../context/I18nContext";
import LanguagePicker from "../components/UI/LanguagePicker";
import "./Login.css";

export default function Login() {
  // Can be a username, email, or phone — whichever the account has set
  // (see the backend's Login handler in user_handler.go).
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const identifierRef = useRef(null);
  const navigate = useNavigate();

  const { login, redirectAfterLogin, setRedirectAfterLogin } = useContext(AuthContext);
  const { systemSettings, setSystemSettings } = useSystemConfig();
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("login");

  // Nobody's signed in yet on this screen, so MainLayout's own
  // /public-config fetch (which only runs once a session is authenticated
  // — see its loadSettings effect) never runs here, and the system name
  // shown below would otherwise only ever be whatever was cached in
  // localStorage from a PREVIOUS session's language. Re-fetching here,
  // keyed on languageCode, is what makes switching the LanguagePicker on
  // this screen actually update the (possibly translated) system name —
  // the backend resolves it for a guest via the ?lang= query param this
  // passes through (see getPublicConfig in services/api.js and
  // currentLangCode's guest fallback in entity_translation_handler.go).
  useEffect(() => {
    getPublicConfig(null, languageCode)
      .then((freshSettings) => {
        if (freshSettings) {
          setSystemSettings(freshSettings);
          localStorage.setItem("systemSettings", JSON.stringify(freshSettings));
        }
      })
      .catch(() => {});
  }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // 1. منطق استخراج البيانات مع Fallback قوي للـ LocalStorage
  const config = useMemo(() => {
    // إذا كانت البيانات موجودة في الـ Context نستخدمها
    if (systemSettings?.appearance?.theme_color) {
      return {
        name: systemSettings.general?.system_name || "POSFLECT",
        color: systemSettings.appearance?.theme_color || "#203664"
      };
    }
    // إذا لم تكن موجودة (لحظة الـ Refresh) نسحبها من الكاش فوراً
    const cached = localStorage.getItem("systemSettings");
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        name: parsed.general?.system_name || "POSFLECT",
        color: parsed.appearance?.theme_color || "#203664"
      };
    }
    return { name: "POSFLECT", color: "#203664" };
  }, [systemSettings]);

  // 2. حقن الألوان في الـ Document Root لضمان وصولها لكل الـ CSS
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', config.color);
    document.documentElement.style.setProperty('--theme-color', config.color);
    
    if (identifierRef.current) identifierRef.current.focus();
  }, [config.color]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      setError(t("validation_error", "Please enter your username, email, or phone, and password"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await apiLogin(identifier, password);
      login(res.token);
      localStorage.setItem("user", JSON.stringify(res.user));
      
      setTimeout(() => {
        const redirectTo = redirectAfterLogin || "/dashboard";
        setRedirectAfterLogin(null);
        navigate(redirectTo, { replace: true });
      }, 300);
    } catch (err) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    // نضع الـ variable هنا أيضاً للاحتياط
    <div className="login-container" style={{ "--theme-color": config.color }}>
      <div className="login-card">
        <div className="flex justify-end mb-2" title={t("language_label", "Language")}>
          <LanguagePicker variant="light" />
        </div>

        <h2 className="login-title" style={{ color: config.color }}>
          {config.name}
        </h2>
        <p className="login-subtitle">{t("subtitle", "Sign in to continue to your dashboard")}</p>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group mb-4">
            <input
              ref={identifierRef}
              type="text"
              autoComplete="username"
              placeholder={t("identifier_placeholder", "Username, Email, or Phone")}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="login-input focus-theme" // الكلاس الذي يحتاج --primary-color
            />
          </div>

          <div className="form-group mb-6">
            <input
              type="password"
              placeholder={t("password_placeholder", "Password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input focus-theme"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={loading}
          >
            {t("submit_button", "Login")}
          </Button>
        </form>

        <p className="login-footer">
          © {new Date().getFullYear()} {config.name} — {t("footer_rights", "All Rights Reserved")}
        </p>
      </div>
    </div>
  );
}